import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import type {
  AgaveAccountResponse,
  AgaveAccountToken,
  AgaveApInvoicesPayload,
  AgaveApInvoicesResult,
  AgaveBankAccountResponse,
  AgaveCostCodeResponse,
  AgaveCostTypeResponse,
  AgaveEmployeeResponse,
  AgaveExpenseResult,
  AgaveGlCodeResponse,
  AgaveJobCostResult,
  AgaveLinkToken,
  AgavePassThroughResponse,
  AgavePassThroughResult,
  AgaveProjectResponse,
  AgaveResolverMutationResult,
  AgaveVendorResponse,
  BasicHttpResponse,
  GetAgaveSyncByPaidolIdPayload,
  GetAgaveGlCodesByCostingPayload,
  MigrateErpEndpointSettingsPayload,
  MutationAgaveApInvoicesCreateExpenseArgs,
  MutationAgaveCreateExpenseArgs,
  MutationAgaveLinkTokenExchangeArgs,
  MutationAgavePostJobCostArgs,
  MutationAgaveResolverMutationArgs,
  MutationMigrateErpEndpointSettingsArgs,
  MutationPersistAgaveDataArgs,
  MutationPostAgavePassThroughArgs,
  MutationStartAcumaticaPostingArgs,
  MutationStartQbdPostingArgs,
  MutationStartQboPostingArgs,
  MutationStartSage300ApInvoiceArgs,
  MutationStartSage300JobCostArgs,
  MutationStartSageIntacctPostingArgs,
  MutationStartVistaPostingArgs,
  QueryGetAgaveAccountArgs,
  QueryGetAgaveApInvoiceByFilterSageIntacctArgs,
  QueryGetAgaveBankAccountsArgs,
  QueryGetAgaveCostCodesArgs,
  QueryGetAgaveCostTypesArgs,
  QueryGetAgaveEmployeesArgs,
  QueryGetAgaveGlCodesArgs,
  QueryGetAgavePassThroughArgs,
  QueryGetAgaveProjectsArgs,
  QueryGetAgaveSyncByPaidolIdArgs,
  QueryGetAgaveVendorByNameArgs,
  QueryGetAgaveVendorsArgs,
  QueryListAgaveCostCodesByPaidolIdArgs,
  QueryListAgaveCostTypesByPaidolIdArgs,
  QueryListAgaveGlCodesByPaidolIdArgs,
  QueryListAgaveProjectsByPaidolIdArgs,
  QueryV2GetAgaveGlCodesByCostingArgs,
  QueryV2SearchAgaveCostCodesArgs,
  QueryV2SearchAgaveCostTypesArgs,
  QueryV2SearchAgaveDepartmentsArgs,
  QueryV2SearchAgaveEmployeesArgs,
  QueryV2SearchAgaveEmCostCodesArgs,
  QueryV2SearchAgaveEmCostTypesArgs,
  QueryV2SearchAgaveEquipmentArgs,
  QueryV2SearchAgaveGlCodesArgs,
  QueryV2SearchAgaveLocationsArgs,
  QueryV2SearchAgaveProjectsArgs,
  QueryV2SearchAgaveSubJobsArgs,
  QueryV2SearchAgaveSubaccountsArgs,
  QueryV2SearchAgaveVendorsArgs,
  SearchAgaveCostCodesPayload,
  SearchAgaveCostTypesPayload,
  SearchAgaveDepartmentsPayload,
  SearchAgaveEmployeesPayload,
  SearchAgaveEmCostCodesPayload,
  SearchAgaveEmCostTypesPayload,
  SearchAgaveEquipmentPayload,
  SearchAgaveGlCodesPayload,
  SearchAgaveLocationsPayload,
  SearchAgaveProjectsPayload,
  SearchAgaveSubJobsPayload,
  SearchAgaveSubaccountsPayload,
  SearchAgaveVendorsPayload,
  StartAcumaticaPostingResponse,
  StartQbdPostingResponse,
  StartQboPostingResponse,
  StartSage300ApInvoiceResponse,
  StartSage300JobCostResponse,
  StartSageIntacctPostingResponse,
  StartVistaPostingResponse,
} from 'CDK_API';

// ─── Queries ────────────────────────────────────────────────────────

const getAgaveProjectsQuery = /* GraphQL */ `
  query GetAgaveProjects($input: AgaveProjectsInput!) {
    getAgaveProjects(input: $input) {
      id
      source_id
      amount
      completion_date
      name
      number
      start_date
      status
      type
      source_create_time
      source_update_time
      source_data
    }
  }
`;

const getAgaveGlCodesQuery = /* GraphQL */ `
  query GetAgaveGlCodes($input: AgaveGlCodesInput!) {
    getAgaveGlCodes(input: $input) {
      id
      source_id
      classification
      currency_code
      current_balance
      description
      name
      number
      status
      type
      source_create_time
      source_update_time
      source_data
    }
  }
`;

const getAgaveCostCodesQuery = /* GraphQL */ `
  query GetAgaveCostCodes($input: AgaveCostCodesInput!) {
    getAgaveCostCodes(input: $input) {
      id
      source_id
      code
      full_code
      name
      parent_id
      project_id
      source_create_time
      source_update_time
      source_data
    }
  }
`;

const getAgaveEmployeesQuery = /* GraphQL */ `
  query GetAgaveEmployees($input: AgaveEmployeesInput!) {
    getAgaveEmployees(input: $input) {
      id
      source_id
      email
      first_name
      full_name
      last_name
      phone
      status
      type
      source_create_time
      source_update_time
      source_data
    }
  }
`;

const getAgaveCostTypesQuery = /* GraphQL */ `
  query GetAgaveCostTypes($input: AgaveCostTypesInput!) {
    getAgaveCostTypes(input: $input) {
      id
      source_id
      code
      name
      project_id
      source_create_time
      source_update_time
      source_data
    }
  }
`;

const getAgaveVendorsQuery = /* GraphQL */ `
  query GetAgaveVendors($input: GetAgaveVendorsInput!) {
    getAgaveVendors(input: $input) {
      id
      source_id
      code
      email
      fax
      name
      phone
      status
      tax_number
      terms
      type
      website
      source_create_time
      source_update_time
      source_data {
        V1099YN
        V1099Type
        V1099Box
        EFT
      }
    }
  }
`;

const getAgaveBankAccountsQuery = /* GraphQL */ `
  query GetAgaveBankAccounts($input: AgaveBankAccountsInput!) {
    getAgaveBankAccounts(input: $input) {
      id
      source_id
      bank_name
      currency_code
      current_balance
      description
      ledger_account_id
      name
      number
      source_create_time
      source_update_time
      status
      type
      source_data
    }
  }
`;

const getAgaveVendorByNameQuery = /* GraphQL */ `
  query GetAgaveVendorByName($input: GetAgaveVendorInput!) {
    getAgaveVendorByName(input: $input) {
      id
      source_id
      code
      email
      fax
      name
      phone
      status
      tax_number
      terms
      type
      website
      source_create_time
      source_update_time
      source_data {
        V1099YN
        V1099Type
        V1099Box
        EFT
      }
    }
  }
`;

const getAgaveAccountQuery = /* GraphQL */ `
  query GetAgaveAccount($input: AgaveAccountInput!) {
    getAgaveAccount(input: $input) {
      account_id
      connection {
        type
      }
      created_at
      linked_company {
        id
        source_id
        name
      }
      linked_user {
        id
        source_id
        name
        email
      }
      reference_id
      source_system {
        name
        slug
      }
      source_system_environment {
        name
        slug
      }
      status
      supports_cross_account_uuid
      supports_cross_company_requests
    }
  }
`;

const getAgavePassThroughQuery = /* GraphQL */ `
  query GetAgavePassThrough($input: AgavePassThroughInput!) {
    getAgavePassThrough(input: $input) {
      status
      content_type
      body
      headers
    }
  }
`;

const getAgaveAPInvoiceByFilterSageIntacctQuery = /* GraphQL */ `
  query GetAgaveAPInvoiceByFilterSageIntacct($input: GetAgaveAPInvoiceByFilterSageIntacctInput!) {
    getAgaveAPInvoiceByFilterSageIntacct(input: $input) {
      id
      source_id
      amount
      amount_due
      batch
      batch_id
      creator_id
      currency_code
      description
      discount_amount
      discount_date
      due_date
      issue_date
      number
      project_id
      retention_amount
      retention_percent
      status
      subtotal_amount
      tax_amount
      total_amount
      type
      vendor_id
      source_create_time
      source_update_time
      source_data
    }
  }
`;

// ─── Mutations ──────────────────────────────────────────────────────

const agaveLinkTokenCreateMutation = /* GraphQL */ `
  mutation AgaveLinkTokenCreate {
    agaveLinkTokenCreate {
      link_token
    }
  }
`;

const agaveLinkTokenExchangeMutation = /* GraphQL */ `
  mutation AgaveLinkTokenExchange($input: AgaveAccountTokenInput!) {
    agaveLinkTokenExchange(input: $input) {
      account_token
      account_id
      connection {
        type
      }
      created_at
      linked_company {
        id
        source_id
        name
      }
      linked_user {
        id
        source_id
        name
        email
      }
      reference_id
      source_system {
        name
        slug
      }
      source_system_environment {
        name
        slug
      }
      status
      supports_cross_account_uuid
      supports_cross_company_requests
    }
  }
`;

const agaveCreateExpenseMutation = /* GraphQL */ `
  mutation AgaveCreateExpense($input: CreateAgaveExpenseInput!) {
    agaveCreateExpense(input: $input) {
      ... on AgaveError {
        message
      }
      ... on CreateAgaveExpenseAPInvoice {
        id
        source_id
        amount
        amount_due
        ap_payment_ids
        attachments {
          id
          source_id
          name
          url
          source_create_time
          source_update_time
        }
        batch
        batch_id
        contract {
          id
          type
        }
        creator_id
        currency_code
        description
        discount_amount
        discount_date
        due_date
        issue_date
        line_items {
          id
          source_id
          amount
          cost_code_id
          cost_type_id
          customer_id
          description
          discount_amount
          item_id
          ledger_account_id
          project_id
          quantity
          retention_amount
          status
          sub_job_id
          tax_amount
          tax_code_id
          type
          unit_cost
          unit_of_measure
          vendor_id
          wbs_id
          source_create_time
          source_update_time
        }
        misc_amount
        net_amount
        number
        project_id
        retention_amount
        retention_percent
        status
        subtotal_amount
        tax_amount
        total_amount
        type
        vendor_id
        source_create_time
        source_update_time
        source_data
      }
    }
  }
`;

const agavePostJobCostMutation = /* GraphQL */ `
  mutation AgavePostJobCost($input: AgavePostJobCostInput!) {
    agavePostJobCost(input: $input) {
      ... on AgaveError {
        message
      }
      ... on AgaveJobCost {
        id
        source_id
        accounting_date
        actual_amount
        actual_quantity
        actual_unit_cost
        actual_unit_of_measure
        attachments
        cost_code_id
        cost_type_id
        description
        ledger_account_id
        name
        number
        posted_date
        prime_contract_id
        project_id
        reference_id
        reference_type
        status
        sub_job_id
        transaction_date
        type
        vendor_id
        wbs_id
        source_create_time
        source_update_time
        source_data
      }
    }
  }
`;

const agaveAPInvoicesCreateExpenseMutation = /* GraphQL */ `
  mutation AgaveAPInvoicesCreateExpense($input: AgaveAPInvoicesCreateExpenseInput!) {
    agaveAPInvoicesCreateExpense(input: $input) {
      ... on AgaveError {
        message
      }
      ... on AgaveAPInvoicesPayload {
        id
        source_id
        amount
        amount_due
        ap_payment_ids
        attachments {
          id
          source_id
          name
          url
          source_create_time
          source_update_time
        }
        batch
        batch_id
        contract {
          id
          type
        }
        creator_id
        currency_code
        description
        discount_amount
        discount_date
        due_date
        issue_date
        line_items {
          id
          source_id
          amount
          cost_code_id
          cost_type_id
          customer_id
          description
          discount_amount
          item_id
          ledger_account_id
          project_id
          quantity
          retention_amount
          status
          sub_job_id
          tax_amount
          tax_code_id
          type
          unit_cost
          unit_of_measure
          vendor_id
          wbs_id
          source_create_time
          source_update_time
        }
        misc_amount
        net_amount
        number
        project_id
        retention_amount
        retention_percent
        status
        subtotal_amount
        tax_amount
        total_amount
        type
        vendor_id
        source_create_time
        source_update_time
        source_data
      }
    }
  }
`;

const agaveResolverMutationMutation = /* GraphQL */ `
  mutation AgaveResolverMutation($input: AgaveResolverMutationInput!) {
    agaveResolverMutation(input: $input) {
      ... on AgaveError {
        message
      }
      ... on AgaveResolverMutationResponse {
        status
        content_type
        body
        headers
      }
    }
  }
`;

const postAgavePassThroughMutation = /* GraphQL */ `
  mutation PostAgavePassThrough($input: AgavePassThroughInput!) {
    postAgavePassThrough(input: $input) {
      ... on AgaveError {
        message
      }
      ... on AgavePassThroughResponse {
        status
        content_type
        body
        headers
      }
    }
  }
`;

const v2AgaveResolverHealthCheckQuery = /* GraphQL */ `
  query V2AgaveResolverHealthCheck($input: String) {
    v2AgaveResolverHealthCheck(input: $input)
  }
`;

// ─── v2Agave Lambda operations (SEE-308) ────────────────────────────
//
// Field selections mirror the equivalent Amplify-side custom queries in
// `src/graphql_custom/queries.js` so the wire shape stays identical during
// cutover.

const v2AgaveHealthCheckQuery = /* GraphQL */ `
  query V2AgaveHealthCheck($input: String) {
    v2AgaveHealthCheck(input: $input)
  }
`;

const v2SearchAgaveGLCodesQuery = /* GraphQL */ `
  query V2SearchAgaveGLCodes($input: SearchAgaveGLCodesInput!) {
    v2SearchAgaveGLCodes(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        classification
        currency_code
        current_balance
        description
        name
        number
        status
        type
        source_create_time
        source_update_time
        createdAt
        updatedAt
        fullName
        source_data {
          data
        }
      }
      nextToken
      total
    }
  }
`;

const v2GetAgaveGlCodesByCostingQuery = /* GraphQL */ `
  query V2GetAgaveGlCodesByCosting($input: GetAgaveGlCodesByCostingInput!) {
    v2GetAgaveGlCodesByCosting(input: $input) {
      items {
        id
        paidolId
        authPaidolId
        source_id
        classification
        currency_code
        current_balance
        description
        name
        number
        status
        type
        source_create_time
        source_update_time
        createdAt
        updatedAt
        fullName
        source_data {
          data
        }
      }
      total
      itemSourceId
      itemName
    }
  }
`;

const v2SearchAgaveProjectsQuery = /* GraphQL */ `
  query V2SearchAgaveProjects($input: SearchAgaveProjectsInput!) {
    v2SearchAgaveProjects(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        amount
        completion_date
        name
        number
        start_date
        status
        type
        source_create_time
        source_update_time
        createdAt
        updatedAt
        fullName
        source_data {
          data
        }
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveCostCodesQuery = /* GraphQL */ `
  query V2SearchAgaveCostCodes($input: SearchAgaveCostCodesInput!) {
    v2SearchAgaveCostCodes(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        asset_account_id
        source_id
        formatted_full_code
        code
        full_code
        cost_unit_of_measure
        description
        expense_account_id
        income_account_id
        name
        parent_id
        project_id
        price_unit_of_measure
        source_create_time
        source_update_time
        status
        type
        unit_cost
        unit_price
        vendor_id
        sub_job_id
        createdAt
        updatedAt
        projectId
        paidolProject
        projectIds
        cost_type_ids
        fullName
        source_data {
          data
        }
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveVendorsQuery = /* GraphQL */ `
  query V2SearchAgaveVendors($input: SearchAgaveVendorsInput!) {
    v2SearchAgaveVendors(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        code
        email
        fax
        name
        phone
        status
        tax_number
        terms
        type
        website
        source_create_time
        source_update_time
        source_data {
          V1099YN
          V1099Type
          V1099Box
          EFT
          data
        }
        createdAt
        updatedAt
        fullName
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveCostTypesQuery = /* GraphQL */ `
  query V2SearchAgaveCostTypes($input: SearchAgaveCostTypesInput!) {
    v2SearchAgaveCostTypes(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        code
        name
        project_id
        source_create_time
        source_update_time
        source_data {
          path
          content_type
          data
        }
        createdAt
        updatedAt
        projectId
        paidolProject
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveSubJobsQuery = /* GraphQL */ `
  query V2SearchAgaveSubJobs($input: SearchAgaveSubJobsInput!) {
    v2SearchAgaveSubJobs(input: $input) {
      items {
        __typename
        id
        agaveSubJobId
        paidolId
        authPaidolId
        source_id
        name
        number
        code
        status
        type
        source_create_time
        source_update_time
        source_data {
          path
          content_type
          data
        }
        createdAt
        updatedAt
        projectId
        fullName
        cost_code_ids
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveLocationsQuery = /* GraphQL */ `
  query V2SearchAgaveLocations($input: SearchAgaveLocationsInput!) {
    v2SearchAgaveLocations(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        name
        fullName
        parent_id
        source_id
        source_create_time
        source_update_time
        createdAt
        updatedAt
        source_data
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveDepartmentsQuery = /* GraphQL */ `
  query V2SearchAgaveDepartments($input: SearchAgaveDepartmentsInput!) {
    v2SearchAgaveDepartments(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        code
        name
        fullName
        status
        type
        source_id
        source_create_time
        source_update_time
        createdAt
        updatedAt
        source_data
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveSubaccountsQuery = /* GraphQL */ `
  query V2SearchAgaveSubaccounts($input: SearchAgaveSubaccountsInput!) {
    v2SearchAgaveSubaccounts(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        code
        name
        status
        type
        source_id
        source_create_time
        source_update_time
        createdAt
        updatedAt
        source_data
      }
      nextToken
      total
    }
  }
`;

// SEE-1435: Vista EM equipment. `emGroup` / `emCo` / `department` are selected
// because the equipment coding cascade scopes the EM cost code and cost type
// dropdowns off the selected equipment's own fields.
const v2SearchAgaveEquipmentQuery = /* GraphQL */ `
  query V2SearchAgaveEquipment($input: SearchAgaveEquipmentInput!) {
    v2SearchAgaveEquipment(input: $input) {
      items {
        # getDisplayName dispatches on __typename; without it these rows fall
        # into its vendor fallback and mobile labels them wrong (SEE-1325).
        __typename
        id
        paidolId
        authPaidolId
        code
        name
        fullName
        emGroup
        emCo
        category
        department
        status
        source_id
        source_create_time
        source_update_time
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

// SEE-1325: the EM cascade. `emGroup` comes off the selected equipment and is
// required; `emCo`/`department` drive the SEE-1435 department scoping, and
// `emCostCodeId` applies the cost-code/cost-type pair Vista enforces at posting.
const v2SearchAgaveEmCostCodesQuery = /* GraphQL */ `
  query V2SearchAgaveEmCostCodes($input: SearchAgaveEmCostCodesInput!) {
    v2SearchAgaveEmCostCodes(input: $input) {
      items {
        # getDisplayName dispatches on __typename — see the equipment query.
        __typename
        id
        paidolId
        authPaidolId
        code
        name
        fullName
        emGroup
        emCostTypeCodes
        source_id
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveEmCostTypesQuery = /* GraphQL */ `
  query V2SearchAgaveEmCostTypes($input: SearchAgaveEmCostTypesInput!) {
    v2SearchAgaveEmCostTypes(input: $input) {
      items {
        # getDisplayName dispatches on __typename — see the equipment query.
        __typename
        id
        paidolId
        authPaidolId
        code
        name
        fullName
        emGroup
        abbreviation
        source_id
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const v2SearchAgaveEmployeesQuery = /* GraphQL */ `
  query V2SearchAgaveEmployees($input: SearchAgaveEmployeesInput!) {
    v2SearchAgaveEmployees(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        name
        fullName
        firstName
        lastName
        email
        status
        source_id
        source_create_time
        source_update_time
        createdAt
        updatedAt
        source_data
      }
      nextToken
      total
    }
  }
`;

// SEE-1237: partition reads replacing the Amplify @index reads of the same name. One page per
// call (limit default 100 server-side), or an exact by-id lookup with `ids` (≤250 per call).
// Each selection set is the Amplify document's, field for field, plus `__typename` and `total`.
const listAgaveProjectsByPaidolIdQuery = /* GraphQL */ `
  query ListAgaveProjectsByPaidolId($input: ListAgaveCodesByPaidolIdInput!) {
    listAgaveProjectsByPaidolId(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        amount
        completion_date
        name
        number
        start_date
        status
        type
        source_create_time
        source_update_time
        source_data {
          data
        }
        relatedIds
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const listAgaveGlCodesByPaidolIdQuery = /* GraphQL */ `
  query ListAgaveGlCodesByPaidolId($input: ListAgaveCodesByPaidolIdInput!) {
    listAgaveGlCodesByPaidolId(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        classification
        currency_code
        current_balance
        description
        name
        number
        status
        type
        source_create_time
        source_update_time
        source_data {
          data
        }
        relatedIds
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const listAgaveCostCodesByPaidolIdQuery = /* GraphQL */ `
  query ListAgaveCostCodesByPaidolId($input: ListAgaveCodesByPaidolIdInput!) {
    listAgaveCostCodesByPaidolId(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        code
        full_code
        name
        parent_id
        project_id
        source_create_time
        source_update_time
        source_data {
          data
        }
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const listAgaveCostTypesByPaidolIdQuery = /* GraphQL */ `
  query ListAgaveCostTypesByPaidolId($input: ListAgaveCodesByPaidolIdInput!) {
    listAgaveCostTypesByPaidolId(input: $input) {
      items {
        __typename
        id
        paidolId
        authPaidolId
        source_id
        code
        name
        project_id
        source_create_time
        source_update_time
        source_data {
          data
        }
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

const getAgaveSyncByPaidolIdQuery = /* GraphQL */ `
  query GetAgaveSyncByPaidolId($input: GetAgaveSyncByPaidolIdInput!) {
    getAgaveSyncByPaidolId(input: $input) {
      sync {
        id
        paidolId
        authPaidolId
        inProgress
        error
        lastRunStart
        lastRunEnd
        taskArn
        syncEntityFilter
        createdAt
        updatedAt
      }
    }
  }
`;

const persistAgaveDataMutation = /* GraphQL */ `
  mutation PersistAgaveData($input: PersistAgaveDataInput!) {
    persistAgaveData(input: $input) {
      statusCode
      body
    }
  }
`;

const migrateERPEndpointSettingsMutation = /* GraphQL */ `
  mutation MigrateERPEndpointSettings($input: String!) {
    migrateERPEndpointSettings(input: $input) {
      migrated
      paidol {
        id
        id_base58
        id_base58_short
        owner_paidol_id
        slug
        legal_name
        account_balance
        name
        dba
        ein
        mcc
        corp_type
        industry
        company_email
        customer_facing_email
        company_phone
        website
        business_owner_ssn
        dwolla_business_industry_classification
        admin_user_id
        low_balance_alert_threshold
        low_balance_auto_funding_amount
        low_balance_auto_funding_threshold
        speedscore
        priority_status
        deleted_at
        company_id
        logo
        priority_response
        agave_data_sync_in_progress
        submittedBankAccounts
        achEnabled
        virtualCardRequested
        virtualCardEnabled
        achRequested
        cron_status
        owner_sub
        is_test_fixture
        is_migration
        enablePCards
        approveWithoutReceipts
        isConstructionType
        agaveAccountToken
        agaveSourceSystemSlug
        isAgaveClient
        erpSpeedchain
        erpConnectionId
        selectedERP
        logoUrl
        isProcoreClient
        creditLimit
        paymentTermDays
        createdAt
        updatedAt
      }
    }
  }
`;

const startSage300APInvoiceMutation = /* GraphQL */ `
  mutation StartSage300APInvoice($input: StartSage300APInvoiceInput!) {
    startSage300APInvoice(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startSage300JobCostMutation = /* GraphQL */ `
  mutation StartSage300JobCost($input: StartSage300JobCostInput!) {
    startSage300JobCost(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startQBOPostingMutation = /* GraphQL */ `
  mutation StartQBOPosting($input: StartQBOPostingInput!) {
    startQBOPosting(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startQBDPostingMutation = /* GraphQL */ `
  mutation StartQBDPosting($input: StartQBDPostingInput!) {
    startQBDPosting(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startSageIntacctPostingMutation = /* GraphQL */ `
  mutation StartSageIntacctPosting($input: StartSageIntacctPostingInput!) {
    startSageIntacctPosting(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startVistaPostingMutation = /* GraphQL */ `
  mutation StartVistaPosting($input: StartVistaPostingInput!) {
    startVistaPosting(input: $input) {
      transactionId
      status
      error
    }
  }
`;

const startAcumaticaPostingMutation = /* GraphQL */ `
  mutation StartAcumaticaPosting($input: StartAcumaticaPostingInput!) {
    startAcumaticaPosting(input: $input) {
      transactionId
      status
      error
    }
  }
`;

// ─── Service ────────────────────────────────────────────────────────

export interface AgaveRequestOptions {
  /**
   * Default true. Pass false for callers that retry on a timer and treat failure as
   * expected (the ERP sync poll), so one flapping endpoint doesn't produce an event per tick.
   * The error still rejects to the caller either way.
   */
  reportErrors?: boolean;
}

export class AgaveService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  // --- Queries ---

  async getAgaveProjects(variables: QueryGetAgaveProjectsArgs): Promise<AgaveProjectResponse[]> {
    return this.executeAndParse<AgaveProjectResponse[]>(getAgaveProjectsQuery, variables, 'getAgaveProjects');
  }

  async getAgaveGlCodes(variables: QueryGetAgaveGlCodesArgs): Promise<AgaveGlCodeResponse[]> {
    return this.executeAndParse<AgaveGlCodeResponse[]>(getAgaveGlCodesQuery, variables, 'getAgaveGlCodes');
  }

  async getAgaveCostCodes(variables: QueryGetAgaveCostCodesArgs): Promise<AgaveCostCodeResponse[]> {
    return this.executeAndParse<AgaveCostCodeResponse[]>(
      getAgaveCostCodesQuery,
      variables,
      'getAgaveCostCodes'
    );
  }

  async getAgaveEmployees(variables: QueryGetAgaveEmployeesArgs): Promise<AgaveEmployeeResponse[]> {
    return this.executeAndParse<AgaveEmployeeResponse[]>(
      getAgaveEmployeesQuery,
      variables,
      'getAgaveEmployees'
    );
  }

  async getAgaveCostTypes(variables: QueryGetAgaveCostTypesArgs): Promise<AgaveCostTypeResponse[]> {
    return this.executeAndParse<AgaveCostTypeResponse[]>(
      getAgaveCostTypesQuery,
      variables,
      'getAgaveCostTypes'
    );
  }

  async getAgaveVendors(variables: QueryGetAgaveVendorsArgs): Promise<AgaveVendorResponse[]> {
    return this.executeAndParse<AgaveVendorResponse[]>(getAgaveVendorsQuery, variables, 'getAgaveVendors');
  }

  async getAgaveBankAccounts(variables: QueryGetAgaveBankAccountsArgs): Promise<AgaveBankAccountResponse[]> {
    return this.executeAndParse<AgaveBankAccountResponse[]>(
      getAgaveBankAccountsQuery,
      variables,
      'getAgaveBankAccounts'
    );
  }

  async getAgaveVendorByName(variables: QueryGetAgaveVendorByNameArgs): Promise<AgaveVendorResponse[]> {
    return this.executeAndParse<AgaveVendorResponse[]>(
      getAgaveVendorByNameQuery,
      variables,
      'getAgaveVendorByName'
    );
  }

  async getAgaveAccount(variables: QueryGetAgaveAccountArgs): Promise<AgaveAccountResponse> {
    return this.executeAndParse<AgaveAccountResponse>(getAgaveAccountQuery, variables, 'getAgaveAccount');
  }

  async getAgavePassThrough(variables: QueryGetAgavePassThroughArgs): Promise<AgavePassThroughResponse[]> {
    return this.executeAndParse<AgavePassThroughResponse[]>(
      getAgavePassThroughQuery,
      variables,
      'getAgavePassThrough'
    );
  }

  async getAgaveAPInvoiceByFilterSageIntacct(
    variables: QueryGetAgaveApInvoiceByFilterSageIntacctArgs
  ): Promise<AgaveApInvoicesPayload[]> {
    return this.executeAndParse<AgaveApInvoicesPayload[]>(
      getAgaveAPInvoiceByFilterSageIntacctQuery,
      variables,
      'getAgaveAPInvoiceByFilterSageIntacct'
    );
  }

  // --- Mutations ---

  async agaveLinkTokenCreate(): Promise<AgaveLinkToken> {
    return this.executeAndParse<AgaveLinkToken>(agaveLinkTokenCreateMutation, {}, 'agaveLinkTokenCreate');
  }

  async agaveLinkTokenExchange(variables: MutationAgaveLinkTokenExchangeArgs): Promise<AgaveAccountToken> {
    return this.executeAndParse<AgaveAccountToken>(
      agaveLinkTokenExchangeMutation,
      variables,
      'agaveLinkTokenExchange'
    );
  }

  async agaveCreateExpense(variables: MutationAgaveCreateExpenseArgs): Promise<AgaveExpenseResult> {
    return this.executeAndParse<AgaveExpenseResult>(
      agaveCreateExpenseMutation,
      variables,
      'agaveCreateExpense'
    );
  }

  async agavePostJobCost(variables: MutationAgavePostJobCostArgs): Promise<AgaveJobCostResult> {
    return this.executeAndParse<AgaveJobCostResult>(agavePostJobCostMutation, variables, 'agavePostJobCost');
  }

  async agaveAPInvoicesCreateExpense(
    variables: MutationAgaveApInvoicesCreateExpenseArgs
  ): Promise<AgaveApInvoicesResult> {
    return this.executeAndParse<AgaveApInvoicesResult>(
      agaveAPInvoicesCreateExpenseMutation,
      variables,
      'agaveAPInvoicesCreateExpense'
    );
  }

  async agaveResolverMutation(
    variables: MutationAgaveResolverMutationArgs
  ): Promise<AgaveResolverMutationResult> {
    return this.executeAndParse<AgaveResolverMutationResult>(
      agaveResolverMutationMutation,
      variables,
      'agaveResolverMutation'
    );
  }

  async postAgavePassThrough(variables: MutationPostAgavePassThroughArgs): Promise<AgavePassThroughResult> {
    return this.executeAndParse<AgavePassThroughResult>(
      postAgavePassThroughMutation,
      variables,
      'postAgavePassThrough'
    );
  }

  // --- Health Check ---

  async v2AgaveResolverHealthCheck(input: string): Promise<string> {
    return this.executeAndParse<string>(
      v2AgaveResolverHealthCheckQuery,
      { input },
      'v2AgaveResolverHealthCheck'
    );
  }

  // --- v2Agave Lambda operations (SEE-308) ---
  //
  // Migrated from Amplify's `API.graphql(graphqlOperation(...))` calls; the
  // CDK Lambda runs the same handlers (verbatim Python copy) so the wire
  // shape is identical.

  async v2AgaveHealthCheck(input: string): Promise<string> {
    return this.executeAndParse<string>(v2AgaveHealthCheckQuery, { input }, 'v2AgaveHealthCheck');
  }

  async v2SearchAgaveGLCodes(variables: QueryV2SearchAgaveGlCodesArgs): Promise<SearchAgaveGlCodesPayload> {
    return this.executeAndParse<SearchAgaveGlCodesPayload>(
      v2SearchAgaveGLCodesQuery,
      variables,
      'v2SearchAgaveGLCodes'
    );
  }

  async v2GetAgaveGlCodesByCosting(
    variables: QueryV2GetAgaveGlCodesByCostingArgs
  ): Promise<GetAgaveGlCodesByCostingPayload> {
    return this.executeAndParse<GetAgaveGlCodesByCostingPayload>(
      v2GetAgaveGlCodesByCostingQuery,
      variables,
      'v2GetAgaveGlCodesByCosting'
    );
  }

  async v2SearchAgaveProjects(
    variables: QueryV2SearchAgaveProjectsArgs
  ): Promise<SearchAgaveProjectsPayload> {
    return this.executeAndParse<SearchAgaveProjectsPayload>(
      v2SearchAgaveProjectsQuery,
      variables,
      'v2SearchAgaveProjects'
    );
  }

  async v2SearchAgaveCostCodes(
    variables: QueryV2SearchAgaveCostCodesArgs
  ): Promise<SearchAgaveCostCodesPayload> {
    return this.executeAndParse<SearchAgaveCostCodesPayload>(
      v2SearchAgaveCostCodesQuery,
      variables,
      'v2SearchAgaveCostCodes'
    );
  }

  async v2SearchAgaveVendors(variables: QueryV2SearchAgaveVendorsArgs): Promise<SearchAgaveVendorsPayload> {
    return this.executeAndParse<SearchAgaveVendorsPayload>(
      v2SearchAgaveVendorsQuery,
      variables,
      'v2SearchAgaveVendors'
    );
  }

  async v2SearchAgaveCostTypes(
    variables: QueryV2SearchAgaveCostTypesArgs
  ): Promise<SearchAgaveCostTypesPayload> {
    return this.executeAndParse<SearchAgaveCostTypesPayload>(
      v2SearchAgaveCostTypesQuery,
      variables,
      'v2SearchAgaveCostTypes'
    );
  }

  async v2SearchAgaveSubJobs(variables: QueryV2SearchAgaveSubJobsArgs): Promise<SearchAgaveSubJobsPayload> {
    return this.executeAndParse<SearchAgaveSubJobsPayload>(
      v2SearchAgaveSubJobsQuery,
      variables,
      'v2SearchAgaveSubJobs'
    );
  }

  async v2SearchAgaveLocations(
    variables: QueryV2SearchAgaveLocationsArgs
  ): Promise<SearchAgaveLocationsPayload> {
    return this.executeAndParse<SearchAgaveLocationsPayload>(
      v2SearchAgaveLocationsQuery,
      variables,
      'v2SearchAgaveLocations'
    );
  }

  async v2SearchAgaveDepartments(
    variables: QueryV2SearchAgaveDepartmentsArgs
  ): Promise<SearchAgaveDepartmentsPayload> {
    return this.executeAndParse<SearchAgaveDepartmentsPayload>(
      v2SearchAgaveDepartmentsQuery,
      variables,
      'v2SearchAgaveDepartments'
    );
  }

  async v2SearchAgaveSubaccounts(
    variables: QueryV2SearchAgaveSubaccountsArgs
  ): Promise<SearchAgaveSubaccountsPayload> {
    return this.executeAndParse<SearchAgaveSubaccountsPayload>(
      v2SearchAgaveSubaccountsQuery,
      variables,
      'v2SearchAgaveSubaccounts'
    );
  }

  async v2SearchAgaveEquipment(
    variables: QueryV2SearchAgaveEquipmentArgs
  ): Promise<SearchAgaveEquipmentPayload> {
    return this.executeAndParse<SearchAgaveEquipmentPayload>(
      v2SearchAgaveEquipmentQuery,
      variables,
      'v2SearchAgaveEquipment'
    );
  }

  async v2SearchAgaveEmCostCodes(
    variables: QueryV2SearchAgaveEmCostCodesArgs
  ): Promise<SearchAgaveEmCostCodesPayload> {
    return this.executeAndParse<SearchAgaveEmCostCodesPayload>(
      v2SearchAgaveEmCostCodesQuery,
      variables,
      'v2SearchAgaveEmCostCodes'
    );
  }

  async v2SearchAgaveEmCostTypes(
    variables: QueryV2SearchAgaveEmCostTypesArgs
  ): Promise<SearchAgaveEmCostTypesPayload> {
    return this.executeAndParse<SearchAgaveEmCostTypesPayload>(
      v2SearchAgaveEmCostTypesQuery,
      variables,
      'v2SearchAgaveEmCostTypes'
    );
  }

  async v2SearchAgaveEmployees(
    variables: QueryV2SearchAgaveEmployeesArgs
  ): Promise<SearchAgaveEmployeesPayload> {
    return this.executeAndParse<SearchAgaveEmployeesPayload>(
      v2SearchAgaveEmployeesQuery,
      variables,
      'v2SearchAgaveEmployees'
    );
  }

  async listAgaveGlCodesByPaidolId(
    variables: QueryListAgaveGlCodesByPaidolIdArgs
  ): Promise<SearchAgaveGlCodesPayload> {
    return this.executeAndParse<SearchAgaveGlCodesPayload>(
      listAgaveGlCodesByPaidolIdQuery,
      variables,
      'listAgaveGlCodesByPaidolId'
    );
  }

  async listAgaveProjectsByPaidolId(
    variables: QueryListAgaveProjectsByPaidolIdArgs
  ): Promise<SearchAgaveProjectsPayload> {
    return this.executeAndParse<SearchAgaveProjectsPayload>(
      listAgaveProjectsByPaidolIdQuery,
      variables,
      'listAgaveProjectsByPaidolId'
    );
  }

  async listAgaveCostCodesByPaidolId(
    variables: QueryListAgaveCostCodesByPaidolIdArgs
  ): Promise<SearchAgaveCostCodesPayload> {
    return this.executeAndParse<SearchAgaveCostCodesPayload>(
      listAgaveCostCodesByPaidolIdQuery,
      variables,
      'listAgaveCostCodesByPaidolId'
    );
  }

  async listAgaveCostTypesByPaidolId(
    variables: QueryListAgaveCostTypesByPaidolIdArgs
  ): Promise<SearchAgaveCostTypesPayload> {
    return this.executeAndParse<SearchAgaveCostTypesPayload>(
      listAgaveCostTypesByPaidolIdQuery,
      variables,
      'listAgaveCostTypesByPaidolId'
    );
  }

  async getAgaveSyncByPaidolId(
    variables: QueryGetAgaveSyncByPaidolIdArgs,
    options?: AgaveRequestOptions
  ): Promise<GetAgaveSyncByPaidolIdPayload> {
    return this.executeAndParse<GetAgaveSyncByPaidolIdPayload>(
      getAgaveSyncByPaidolIdQuery,
      variables,
      'getAgaveSyncByPaidolId',
      options
    );
  }

  async persistAgaveData(variables: MutationPersistAgaveDataArgs): Promise<BasicHttpResponse> {
    return this.executeAndParse<BasicHttpResponse>(persistAgaveDataMutation, variables, 'persistAgaveData');
  }

  async migrateERPEndpointSettings(
    variables: MutationMigrateErpEndpointSettingsArgs
  ): Promise<MigrateErpEndpointSettingsPayload> {
    return this.executeAndParse<MigrateErpEndpointSettingsPayload>(
      migrateERPEndpointSettingsMutation,
      variables,
      'migrateERPEndpointSettings'
    );
  }

  async startSage300APInvoice(
    variables: MutationStartSage300ApInvoiceArgs
  ): Promise<StartSage300ApInvoiceResponse> {
    return this.executeAndParse<StartSage300ApInvoiceResponse>(
      startSage300APInvoiceMutation,
      variables,
      'startSage300APInvoice'
    );
  }

  async startSage300JobCost(
    variables: MutationStartSage300JobCostArgs
  ): Promise<StartSage300JobCostResponse> {
    return this.executeAndParse<StartSage300JobCostResponse>(
      startSage300JobCostMutation,
      variables,
      'startSage300JobCost'
    );
  }

  async startQBOPosting(variables: MutationStartQboPostingArgs): Promise<StartQboPostingResponse> {
    return this.executeAndParse<StartQboPostingResponse>(
      startQBOPostingMutation,
      variables,
      'startQBOPosting'
    );
  }

  async startQBDPosting(variables: MutationStartQbdPostingArgs): Promise<StartQbdPostingResponse> {
    return this.executeAndParse<StartQbdPostingResponse>(
      startQBDPostingMutation,
      variables,
      'startQBDPosting'
    );
  }

  async startSageIntacctPosting(
    variables: MutationStartSageIntacctPostingArgs
  ): Promise<StartSageIntacctPostingResponse> {
    return this.executeAndParse<StartSageIntacctPostingResponse>(
      startSageIntacctPostingMutation,
      variables,
      'startSageIntacctPosting'
    );
  }

  async startVistaPosting(variables: MutationStartVistaPostingArgs): Promise<StartVistaPostingResponse> {
    return this.executeAndParse<StartVistaPostingResponse>(
      startVistaPostingMutation,
      variables,
      'startVistaPosting'
    );
  }

  async startAcumaticaPosting(
    variables: MutationStartAcumaticaPostingArgs
  ): Promise<StartAcumaticaPostingResponse> {
    return this.executeAndParse<StartAcumaticaPostingResponse>(
      startAcumaticaPostingMutation,
      variables,
      'startAcumaticaPosting'
    );
  }

  // --- Private helpers ---

  private async executeAndParse<T>(
    query: string,
    variables: object,
    operationName: string,
    options?: AgaveRequestOptions
  ): Promise<T> {
    const result = await this.execute<Record<string, T>>(query, variables, operationName, options);
    const value = result[operationName];
    if (value === undefined || value === null) {
      // GraphQL returned data without the operation field — server-side schema mismatch
      // or a resolver that silently returned null. Fail loud so callers don't downstream
      // a misleading `null as T` (which produces TypeErrors on property access).
      throw new Error(`No data returned for operation ${operationName}`);
    }
    return value;
  }

  private async execute<T>(
    query: string,
    variables: object,
    operationName: string,
    { reportErrors = true }: AgaveRequestOptions = {}
  ): Promise<T> {
    // Not gated on reportErrors: a missing endpoint is a deployment fault, not a flaky call.
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'agave', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      const body = JSON.stringify({ query, variables });

      const response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: jwt,
        },
        body,
      });

      let json: { data?: T; errors?: Array<{ message: string }> };
      try {
        json = await response.json();
      } catch {
        throw new Error(`HTTP ${response.status}: non-JSON response`);
      }

      reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName });

      if (!response.ok) {
        const message =
          json?.errors?.map((e: { message: string }) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        if (reportErrors) {
          captureException(error, {
            tags: { area: 'agave', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
            extra: {
              operation: operationName,
              endpoint: this.cdkAppSyncEndpoint,
            },
          });
        }
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
}

export const agaveService = new AgaveService();
